import { useEffect, useRef, useState } from "react";
import { Box, Button, Modal, Text, Title, Stack } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";

interface Firework {
  x: number;
  y: number;
  particles: Particle[];
  color: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
}

export const NewYearCelebration = () => {
  const [opened, setOpened] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const animationFrameRef = useRef<number>();
  const fireworksRef = useRef<Firework[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const keySequenceRef = useRef<string[]>([]);

  const showCelebration = () => {
    setOpened(true);
    startFireworks();
    playMusic();
  };

  const playMusic = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 26; // Bắt đầu từ giây thứ 26
      audioRef.current.play().catch((error) => {
        console.error("Lỗi phát nhạc:", error);
      });
    }
  };

  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // 0-11 -> 1-12
    const day = today.getDate();

    // Chỉ hiển thị vào ngày 1/1/2026
    if (year !== 2026 || month !== 1 || day !== 1) {
      return;
    }

    // Check localStorage
    const shownKey = "newYear2026Shown";
    const shownDate = localStorage.getItem(shownKey);

    if (shownDate === "2026-01-01") {
      return; // Đã hiển thị rồi
    }

    // Hiển thị popup
    showCelebration();
    localStorage.setItem(shownKey, "2026-01-01");
  }, []);

  // Keyboard listener để test với tổ hợp phím "it01"
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      keySequenceRef.current.push(key);

      // Giữ chỉ 4 ký tự gần nhất
      if (keySequenceRef.current.length > 4) {
        keySequenceRef.current.shift();
      }

      // Kiểm tra tổ hợp phím "it01"
      if (keySequenceRef.current.join("") === "it01") {
        showCelebration();
        keySequenceRef.current = []; // Reset sequence
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startFireworks = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = [
      "#ff0000",
      "#00ff00",
      "#0000ff",
      "#ffff00",
      "#ff00ff",
      "#00ffff",
      "#ff8800",
      "#88ff00",
    ];

    const createFirework = (x: number, y: number) => {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const particles: Particle[] = [];

      for (let i = 0; i < 50; i++) {
        const angle = (Math.PI * 2 * i) / 50;
        const speed = 2 + Math.random() * 4;
        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          maxLife: 60 + Math.random() * 40,
          color,
        });
      }

      fireworksRef.current.push({ x, y, particles, color });
    };

    // Tạo pháo hoa ngẫu nhiên
    const interval = setInterval(() => {
      const x = Math.random() * canvas.width;
      const y = Math.random() * (canvas.height * 0.6) + canvas.height * 0.2;
      createFirework(x, y);
    }, 500);

    const animate = () => {
      // Không cần nền đen, để trong suốt để thấy nền trang web
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Cập nhật và vẽ pháo hoa
      fireworksRef.current = fireworksRef.current.filter((firework) => {
        firework.particles = firework.particles.filter((particle) => {
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.vy += 0.1; // Gravity
          particle.life++;

          const alpha = 1 - particle.life / particle.maxLife;
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.fillStyle = particle.color;
          ctx.shadowBlur = 10;
          ctx.shadowColor = particle.color;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          return particle.life < particle.maxLife;
        });

        return firework.particles.length > 0;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Dọn dẹp sau 10 giây
    setTimeout(() => {
      clearInterval(interval);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, 10000);
  };

  const handleClose = () => {
    setOpened(false);
    // Dừng nhạc khi đóng popup
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 26; // Reset về giây 26
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src="/nhactet.mp3"
        loop
        preload="auto"
        style={{ display: "none" }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 9998,
          background: "transparent",
        }}
      />
      <Modal
        opened={opened}
        onClose={handleClose}
        centered
        size="lg"
        withCloseButton={false}
        overlayProps={{
          backgroundOpacity: 0.3,
          blur: 3,
        }}
        styles={{
          content: {
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "3px solid #ffd700",
            borderRadius: "20px",
          },
          body: {
            padding: "40px",
          },
        }}
      >
        <Stack align="center" gap="xl" py="md">
          <IconSparkles size={80} color="#ffd700" stroke={2} />
          <Title
            order={1}
            c="white"
            ta="center"
            style={{
              fontSize: "2.5rem",
              fontWeight: 900,
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              lineHeight: 1.2,
            }}
          >
            🎉 Chúc Mừng Năm Mới 2026! 🎉
          </Title>
          <Box ta="center" style={{ maxWidth: "500px" }}>
            <Text
              size="lg"
              c="white"
              ta="center"
              fw={500}
              style={{
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                lineHeight: 1.6,
              }}
            >
              Chúc bạn một năm mới tràn đầy
            </Text>
            <Text
              size="lg"
              c="white"
              ta="center"
              fw={500}
              style={{
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                lineHeight: 1.6,
              }}
            >
              hạnh phúc, thành công và may mắn!
            </Text>
          </Box>
          <Text
            size="xl"
            c="#ffd700"
            ta="center"
            fw={700}
            style={{
              textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
              letterSpacing: "1px",
            }}
          >
            ✨ An Khang Thịnh Vượng ✨
          </Text>
          <Button
            size="lg"
            onClick={handleClose}
            mt="md"
            style={{
              background: "linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)",
              color: "#333",
              fontWeight: 700,
              padding: "14px 50px",
              borderRadius: "50px",
              boxShadow: "0 4px 15px rgba(255, 215, 0, 0.4)",
            }}
          >
            Cảm ơn bạn! 🎊
          </Button>
        </Stack>
      </Modal>
    </>
  );
};

